import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { CALLBACK_PATH, START_PATH, nativeBrowserOAuth, readBrowserHandoff } from "@/lib/nativeAuthHandoff";
import { hasProviderReturn, isNewSignInFor } from "@/lib/nativeHandoffBrowser";

/**
 * In the in-app browser used for app sign-in only: if the provider returns to
 * any other page of our site (for example the home page), send it to the
 * callback page, which hands the session to the app. Without this the person
 * was left browsing the full website, signed in, inside the sign-in sheet.
 * Does nothing in normal browsers or inside the apps.
 */
export const NativeHandoffBrowserGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!nativeBrowserOAuth.enabled || Capacitor.isNativePlatform()) return;
    if (location.pathname === START_PATH || location.pathname === CALLBACK_PATH) return;
    const record = readBrowserHandoff();
    if (!record) return;
    let cancelled = false;
    const search = location.search;
    const hash = location.hash;
    (async () => {
      const returned = hasProviderReturn(search, hash);
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (returned || isNewSignInFor(record, data.session)) {
        // Carry a provider error across, never tokens (the session is already stored).
        const carry = /(?:^|[?&#])error=/.test(`${search}${hash}`) ? `${search}${hash}` : "";
        navigate(`${CALLBACK_PATH}${carry}`, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
};
